// GrapesJS GPT Plugin - AI-Powered Content Generation
// Integrates OpenAI's GPT technology for seamless content creation and editing

import type { GrapesJSEditor } from '../types/grapesjs';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:4000';

interface GptPluginOptions {
  openaiApiKey?: string | null;
  model?: string;
  maxTokens?: number;
  temperature?: number;
  magicIcon?: string;
  buttonPosition?: string;
  buttonSize?: number;
  zIndex?: number;
  disableFloatingButton?: boolean;
  contentTypes?: string[];
  defaultPrompts?: Record<string, string>;
}

export default function grapesjsGptPlugin(editor: GrapesJSEditor, opts: GptPluginOptions = {}): void {
  const options = {
    // OpenAI API configuration
    openaiApiKey: opts.openaiApiKey || null,
    model: opts.model || 'gpt-4',
    maxTokens: opts.maxTokens || 1000,
    temperature: opts.temperature || 0.7,
    
    // UI configuration
    magicIcon: opts.magicIcon || '✨',
    buttonPosition: opts.buttonPosition || 'top-right',
    buttonSize: opts.buttonSize || 40,
    zIndex: opts.zIndex || 99999,
    disableFloatingButton: opts.disableFloatingButton || false, // New option to disable floating button
    
    // Content types
    contentTypes: opts.contentTypes || [
      'headlines',
      'paragraphs', 
      'descriptions',
      'call-to-actions',
      'meta-descriptions',
      'social-media',
      'email-subject-lines',
      'product-descriptions'
    ],
    
    // Default prompts
    defaultPrompts: opts.defaultPrompts || {
      headlines: 'Generate a compelling headline for a {topic}',
      paragraphs: 'Write a professional paragraph about {topic}',
      descriptions: 'Create a detailed description for {topic}',
      'call-to-actions': 'Generate a persuasive call-to-action for {topic}',
      'meta-descriptions': 'Write an SEO-optimized meta description for {topic}',
      'social-media': 'Create engaging social media content for {topic}',
      'email-subject-lines': 'Generate email subject lines for {topic}',
      'product-descriptions': 'Write a product description for {topic}'
    }
  };

  let gptPanel: HTMLElement | null = null;
  let selectedComponent: any = null;
  let isGenerating: boolean = false;

  // API key is now managed server-side — always proceed with backend proxy
  function checkApiKey(): boolean {
    return true;
  }

  // Create magic icon button
  function createMagicButton(): HTMLElement {
    const button = document.createElement('div');
    button.className = 'gpt-magic-button';
    button.innerHTML = `
      <div class="gpt-magic-icon">
        <span>${options.magicIcon}</span>
      </div>
      <div class="gpt-magic-tooltip">
        <span>AI Content Generator</span>
        <small>Powered by GPT</small>
      </div>
    `;
    
    button.style.cssText = `
      position: fixed;
      ${options.buttonPosition.includes('top') ? 'top: 20px;' : 'bottom: 20px;'}
      ${options.buttonPosition.includes('right') ? 'right: 20px;' : 'left: 20px;'}
      width: ${options.buttonSize}px;
      height: ${options.buttonSize}px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 50%;
      cursor: pointer;
      z-index: ${options.zIndex};
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
      transition: all 0.3s ease;
      user-select: none;
      border: 2px solid white;
    `;

    // Hover effects
    button.addEventListener('mouseenter', () => {
      button.style.transform = 'scale(1.1)';
      button.style.boxShadow = '0 6px 25px rgba(102, 126, 234, 0.6)';
    });

    button.addEventListener('mouseleave', () => {
      button.style.transform = 'scale(1)';
      button.style.boxShadow = '0 4px 20px rgba(102, 126, 234, 0.4)';
    });

    // Click handler
    button.addEventListener('click', () => {
      showGptPanel();
    });

    // Magic icon styling
    const icon = button.querySelector('.gpt-magic-icon');
    icon.style.cssText = `
      font-size: 20px;
      color: white;
      text-align: center;
      line-height: 1;
    `;

    // Tooltip styling
    const tooltip = button.querySelector('.gpt-magic-tooltip');
    tooltip.style.cssText = `
      position: absolute;
      right: 50px;
      top: 50%;
      transform: translateY(-50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 12px;
      white-space: nowrap;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.3s ease;
      z-index: ${options.zIndex + 1};
    `;

    // Show tooltip on hover
    button.addEventListener('mouseenter', () => {
      tooltip.style.opacity = '1';
    });

    button.addEventListener('mouseleave', () => {
      tooltip.style.opacity = '0';
    });

    return button;
  }

  // Create GPT panel
  function createGptPanel() {
    const panel = document.createElement('div');
    panel.className = 'gpt-panel';
    panel.innerHTML = `
      <div class="gpt-panel-header">
        <div class="gpt-panel-title">
          <span class="gpt-panel-icon">✨</span>
          <h3>AI Content Generator</h3>
        </div>
        <button class="gpt-panel-close" onclick="closeGptPanel()">×</button>
      </div>
      
      <div class="gpt-panel-content">
        <div class="gpt-section">
          <label>Content Type</label>
          <select id="gpt-content-type" class="gpt-select">
            ${options.contentTypes.map(type => 
              `<option value="${type}">${type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}</option>`
            ).join('')}
          </select>
        </div>
        
        <div class="gpt-section">
          <label>Topic/Context</label>
          <input type="text" id="gpt-topic" class="gpt-input" placeholder="Enter your topic or context...">
        </div>
        
        <div class="gpt-section">
          <label>Custom Prompt (Optional)</label>
          <textarea id="gpt-custom-prompt" class="gpt-textarea" placeholder="Or write your own custom prompt..."></textarea>
        </div>
        
        <div class="gpt-section">
          <label>Generation Options</label>
          <div class="gpt-options">
            <div class="gpt-option">
              <label>Creativity Level</label>
              <input type="range" id="gpt-temperature" min="0" max="1" step="0.1" value="${options.temperature}">
              <span id="gpt-temperature-value">${options.temperature}</span>
            </div>
            <div class="gpt-option">
              <label>Max Length</label>
              <input type="range" id="gpt-max-tokens" min="100" max="2000" step="100" value="${options.maxTokens}">
              <span id="gpt-max-tokens-value">${options.maxTokens}</span>
            </div>
          </div>
        </div>
        
        <div class="gpt-actions">
          <button id="gpt-generate-btn" class="gpt-generate-btn" onclick="generateContent()">
            <span class="gpt-btn-icon">✨</span>
            Generate Content
          </button>
          <button id="gpt-modify-btn" class="gpt-modify-btn" onclick="modifyContent()" style="display: none;">
            <span class="gpt-btn-icon">✏️</span>
            Modify Selected
          </button>
        </div>
        
        <div id="gpt-result" class="gpt-result" style="display: none;">
          <div class="gpt-result-header">
            <h4>Generated Content</h4>
            <button onclick="copyToClipboard()" class="gpt-copy-btn">📋 Copy</button>
          </div>
          <div id="gpt-result-content" class="gpt-result-content"></div>
          <div class="gpt-result-actions">
            <button onclick="applyToComponent()" class="gpt-apply-btn">Apply to Component</button>
            <button onclick="regenerateContent()" class="gpt-regenerate-btn">Regenerate</button>
          </div>
        </div>
        
        <div id="gpt-loading" class="gpt-loading" style="display: none;">
          <div class="gpt-spinner"></div>
          <p>Generating content with AI...</p>
        </div>
      </div>
    `;

    // Panel styling
    panel.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 500px;
      max-width: 90vw;
      max-height: 80vh;
      background: white;
      border-radius: 12px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      z-index: ${options.zIndex + 10};
      display: none;
      overflow: hidden;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    // Add panel styles
    const styles = document.createElement('style');
    styles.textContent = `
      .gpt-panel-header {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 20px;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      
      .gpt-panel-title {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      
      .gpt-panel-title h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
      }
      
      .gpt-panel-icon {
        font-size: 24px;
      }
      
      .gpt-panel-close {
        background: none;
        border: none;
        color: white;
        font-size: 24px;
        cursor: pointer;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: background 0.3s ease;
      }
      
      .gpt-panel-close:hover {
        background: rgba(255, 255, 255, 0.2);
      }
      
      .gpt-panel-content {
        padding: 20px;
        max-height: 60vh;
        overflow-y: auto;
      }
      
      .gpt-section {
        margin-bottom: 20px;
      }
      
      .gpt-section label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
        color: #333;
      }
      
      .gpt-select, .gpt-input, .gpt-textarea {
        width: 100%;
        padding: 12px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 14px;
        transition: border-color 0.3s ease;
      }
      
      .gpt-select:focus, .gpt-input:focus, .gpt-textarea:focus {
        outline: none;
        border-color: #667eea;
      }
      
      .gpt-textarea {
        min-height: 80px;
        resize: vertical;
      }
      
      .gpt-options {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 15px;
      }
      
      .gpt-option {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      
      .gpt-option input[type="range"] {
        width: 100%;
      }
      
      .gpt-actions {
        display: flex;
        gap: 10px;
        margin-top: 20px;
      }
      
      .gpt-generate-btn, .gpt-modify-btn {
        flex: 1;
        padding: 12px 20px;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        transition: all 0.3s ease;
      }
      
      .gpt-generate-btn {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
      }
      
      .gpt-modify-btn {
        background: #f8f9fa;
        color: #333;
        border: 1px solid #ddd;
      }
      
      .gpt-generate-btn:hover, .gpt-modify-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
      
      .gpt-result {
        margin-top: 20px;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        overflow: hidden;
      }
      
      .gpt-result-header {
        background: #f8f9fa;
        padding: 15px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #e0e0e0;
      }
      
      .gpt-result-header h4 {
        margin: 0;
        color: #333;
      }
      
      .gpt-copy-btn {
        background: none;
        border: 1px solid #ddd;
        padding: 6px 12px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
        transition: all 0.3s ease;
      }
      
      .gpt-copy-btn:hover {
        background: #f0f0f0;
      }
      
      .gpt-result-content {
        padding: 15px;
        background: white;
        min-height: 100px;
        max-height: 200px;
        overflow-y: auto;
        line-height: 1.6;
        color: #333;
      }
      
      .gpt-result-actions {
        padding: 15px;
        display: flex;
        gap: 10px;
        background: #f8f9fa;
        border-top: 1px solid #e0e0e0;
      }
      
      .gpt-apply-btn, .gpt-regenerate-btn {
        flex: 1;
        padding: 8px 16px;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-size: 13px;
        transition: all 0.3s ease;
      }
      
      .gpt-apply-btn {
        background: #28a745;
        color: white;
      }
      
      .gpt-regenerate-btn {
        background: #6c757d;
        color: white;
      }
      
      .gpt-loading {
        text-align: center;
        padding: 40px 20px;
        color: #666;
      }
      
      .gpt-spinner {
        width: 40px;
        height: 40px;
        border: 4px solid #f3f3f3;
        border-top: 4px solid #667eea;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto 20px;
      }
      
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      
      .gpt-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        z-index: ${options.zIndex + 5};
        display: none;
      }
    `;

    document.head.appendChild(styles);
    return panel;
  }

  // Show GPT panel
  function showGptPanel() {
    if (!gptPanel) {
      gptPanel = createGptPanel();
      document.body.appendChild(gptPanel);
      
      // Create overlay
      const overlay = document.createElement('div');
      overlay.className = 'gpt-overlay';
      overlay.onclick = closeGptPanel;
      document.body.appendChild(overlay);
      
      // Add event listeners
      setupEventListeners();
    }

    // Defensive check for selected component
    const selected = editor.getSelected();
    const modifyBtn = document.getElementById('gpt-modify-btn');

    if (
      selected &&
      (typeof selected.is === 'function' && selected.is('text') ||
        (typeof selected.get === 'function' && selected.get('content')))
    ) {
      modifyBtn.style.display = 'block';
      selectedComponent = selected;
    } else {
      modifyBtn.style.display = 'none';
      selectedComponent = null;
    }

    gptPanel.style.display = 'block';
    document.querySelector('.gpt-overlay').style.display = 'block';
  }

  // Close GPT panel
  function closeGptPanel() {
    if (gptPanel) {
      gptPanel.style.display = 'none';
      const overlay = document.querySelector('.gpt-overlay');
      if (overlay) overlay.style.display = 'none';
    }
  }

  // Setup event listeners
  function setupEventListeners() {
    // Temperature slider
    const tempSlider = document.getElementById('gpt-temperature');
    const tempValue = document.getElementById('gpt-temperature-value');
    tempSlider.addEventListener('input', (e) => {
      tempValue.textContent = e.target.value;
    });

    // Max tokens slider
    const tokensSlider = document.getElementById('gpt-max-tokens');
    const tokensValue = document.getElementById('gpt-max-tokens-value');
    tokensSlider.addEventListener('input', (e) => {
      tokensValue.textContent = e.target.value;
    });

    // Content type change
    const contentTypeSelect = document.getElementById('gpt-content-type');
    const topicInput = document.getElementById('gpt-topic');
    const customPrompt = document.getElementById('gpt-custom-prompt');

    contentTypeSelect.addEventListener('change', () => {
      const selectedType = contentTypeSelect.value;
      const defaultPrompt = options.defaultPrompts[selectedType];
      if (defaultPrompt) {
        customPrompt.placeholder = defaultPrompt;
      }
    });
  }

  // Generate content with GPT
  async function generateContent() {
    if (isGenerating) return;

    const contentType = document.getElementById('gpt-content-type').value;
    const topic = document.getElementById('gpt-topic').value;
    const customPrompt = document.getElementById('gpt-custom-prompt').value;
    const temperature = parseFloat(document.getElementById('gpt-temperature').value);
    const maxTokens = parseInt(document.getElementById('gpt-max-tokens').value);

    if (!topic && !customPrompt) {
      showNotification('Please enter a topic or custom prompt', 'error');
      return;
    }

    isGenerating = true;
    showLoading(true);

    try {
      let prompt = customPrompt;
      if (!prompt) {
        prompt = options.defaultPrompts[contentType].replace('{topic}', topic);
      }

      // Add context awareness if AI Context Enhancer is available
      if (window.generateContextAwarePrompt) {
        const context = {
          contentType: contentType,
          topic: topic,
          selectedComponent: selectedComponent ? {
            type: selectedComponent.get('type'),
            tagName: selectedComponent.get('tagName'),
            className: selectedComponent.get('className'),
            content: selectedComponent.get('content')
          } : null,
          editorState: {
            device: editor.getDevice(),
            currentPage: editor.Pages?.getSelected()?.getMainComponent()?.get('name') ?? 'Page'
          }
        };

        prompt = window.generateContextAwarePrompt(prompt, context);
      }

      const response = await callOpenAI(prompt, temperature, maxTokens);

      if (response) {
        showResult(response);
      } else {
        showNotification('Failed to generate content. Please try again.', 'error');
      }
    } catch (error) {
      console.error('GPT generation error:', error);
      showNotification('Error generating content: ' + error.message, 'error');
    } finally {
      isGenerating = false;
      showLoading(false);
    }
  }

  // Modify existing content
  async function modifyContent() {
    if (!selectedComponent || isGenerating) return;

    const contentType = document.getElementById('gpt-content-type').value;
    const topic = document.getElementById('gpt-topic').value;
    const customPrompt = document.getElementById('gpt-custom-prompt').value;
    const temperature = parseFloat(document.getElementById('gpt-temperature').value);
    const maxTokens = parseInt(document.getElementById('gpt-max-tokens').value);

    const currentContent = selectedComponent.get('content') || '';
    
    if (!currentContent.trim()) {
      showNotification('No content to modify. Please select a component with text content.', 'error');
      return;
    }

    isGenerating = true;
    showLoading(true);

    try {
      let prompt = customPrompt;
      if (!prompt) {
        const basePrompt = options.defaultPrompts[contentType] || 'Improve this content: {content}';
        prompt = basePrompt.replace('{topic}', topic).replace('{content}', currentContent);
      } else {
        prompt = `${prompt}\n\nCurrent content: ${currentContent}`;
      }

      // Add context awareness if AI Context Enhancer is available
      if (window.generateContextAwarePrompt) {
        const context = {
          contentType: contentType,
          topic: topic,
          selectedComponent: {
            type: selectedComponent.get('type'),
            tagName: selectedComponent.get('tagName'),
            className: selectedComponent.get('className'),
            content: currentContent
          },
          editorState: {
            device: editor.getDevice(),
            currentPage: editor.Pages?.getSelected()?.getMainComponent()?.get('name') ?? 'Page'
          },
          modification: true
        };
        
        prompt = window.generateContextAwarePrompt(prompt, context);
      }

      const response = await callOpenAI(prompt, temperature, maxTokens);

      if (response) {
        showResult(response);
      } else {
        showNotification('Failed to modify content. Please try again.', 'error');
      }
    } catch (error) {
      console.error('GPT modification error:', error);
      showNotification('Error modifying content: ' + error.message, 'error');
    } finally {
      isGenerating = false;
      showLoading(false);
    }
  }

  // Proxy AI content generation through the backend so the OpenAI key never reaches the browser
  async function callOpenAI(prompt: string, temperature: number, maxTokens: number): Promise<string | null> {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('jwt') || '';
      const response = await fetch(`${API_BASE}/api/ai/generate-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ prompt, model: options.model, maxTokens, temperature })
      });

      if (!response.ok) {
        const err: any = await response.json().catch(() => ({}));
        throw new Error(err.error || `Backend error ${response.status}`);
      }

      const data = await response.json();
      return data.content;
    } catch (error: any) {
      console.error('AI proxy call failed, using demo response:', error);
      return generateMockResponse(prompt);
    }
  }

  // Generate mock response for demo
  function generateMockResponse(prompt) {
    const mockResponses = {
      headlines: [
        'Transform Your Business with Revolutionary Solutions',
        'Unlock the Power of Innovation Today',
        'Experience Excellence Like Never Before',
        'Discover What Sets Us Apart from the Competition'
      ],
      paragraphs: [
        'In today\'s rapidly evolving digital landscape, businesses need innovative solutions that drive growth and efficiency. Our comprehensive platform provides the tools and insights you need to stay ahead of the competition and achieve your goals.',
        'We understand that every business is unique, which is why we offer customizable solutions tailored to your specific needs. Our team of experts works closely with you to develop strategies that deliver measurable results.',
        'With years of experience and a proven track record of success, we have helped countless organizations transform their operations and achieve sustainable growth. Our commitment to excellence and customer satisfaction sets us apart in the industry.'
      ],
      'call-to-actions': [
        'Ready to get started? Contact us today for a free consultation.',
        'Don\'t wait any longer. Take the first step towards success now.',
        'Join thousands of satisfied customers. Start your journey today.',
        'Experience the difference. Request your demo now.'
      ]
    };

    const contentType = Object.keys(mockResponses).find(type => 
      prompt.toLowerCase().includes(type)
    );

    if (contentType && mockResponses[contentType]) {
      const responses = mockResponses[contentType];
      return responses[Math.floor(Math.random() * responses.length)];
    }

    return 'This is a sample AI-generated content. In a real implementation, this would be generated by OpenAI\'s GPT model based on your specific prompt and requirements.';
  }

  // Show loading state
  function showLoading(show) {
    const loading = document.getElementById('gpt-loading');
    const result = document.getElementById('gpt-result');
    const generateBtn = document.getElementById('gpt-generate-btn');
    const modifyBtn = document.getElementById('gpt-modify-btn');

    if (show) {
      loading.style.display = 'block';
      result.style.display = 'none';
      generateBtn.disabled = true;
      modifyBtn.disabled = true;
    } else {
      loading.style.display = 'none';
      generateBtn.disabled = false;
      modifyBtn.disabled = false;
    }
  }

  // Show result
  function showResult(content) {
    const result = document.getElementById('gpt-result');
    const resultContent = document.getElementById('gpt-result-content');
    
    resultContent.textContent = content;
    result.style.display = 'block';
    
    // Store generated content for later use
    window.generatedContent = content;
  }

  // Copy to clipboard
  function copyToClipboard() {
    const content = window.generatedContent;
    if (content) {
      navigator.clipboard.writeText(content).then(() => {
        const copyBtn = document.querySelector('.gpt-copy-btn');
        const originalText = copyBtn.textContent;
        copyBtn.textContent = '✅ Copied!';
        setTimeout(() => {
          copyBtn.textContent = originalText;
        }, 2000);
      });
    }
  }

  // Apply to component
  function applyToComponent() {
    const content = window.generatedContent;
    if (!content) return;

    if (selectedComponent) {
      // Apply to selected component
      selectedComponent.set('content', content);
      editor.refresh();
      closeGptPanel();
      showNotification('Content applied to component!', 'success');
    } else {
      // Create new text component
      const component = editor.DomComponents.addComponent({
        type: 'text',
        content: content,
        style: {
          padding: '20px',
          'font-family': 'Arial, sans-serif',
          'line-height': '1.6'
        }
      });
      
      editor.select(component);
      closeGptPanel();
      showNotification('New text component created!', 'success');
    }
  }

  // Regenerate content
  function regenerateContent() {
    const contentType = document.getElementById('gpt-content-type').value;
    const topic = document.getElementById('gpt-topic').value;
    const customPrompt = document.getElementById('gpt-custom-prompt').value;

    if (selectedComponent) {
      modifyContent();
    } else {
      generateContent();
    }
  }

  // Show notification
  function showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `gpt-notification gpt-notification-${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#28a745' : '#007bff'};
      color: white;
      padding: 12px 20px;
      border-radius: 6px;
      z-index: ${options.zIndex + 20};
      animation: slideIn 0.3s ease;
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }

  // Add notification animations
  const notificationStyles = document.createElement('style');
  notificationStyles.textContent = `
    @keyframes slideIn {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    @keyframes slideOut {
      from { transform: translateX(0); opacity: 1; }
      to { transform: translateX(100%); opacity: 0; }
    }
  `;
  document.head.appendChild(notificationStyles);

  // Initialize plugin
  function init() {
    console.log('✨ Initializing GrapesJS GPT Plugin...');
    
    // Add magic button only if not disabled
    if (!options.disableFloatingButton) {
      const magicButton = createMagicButton();
      document.body.appendChild(magicButton);
    }
    
    // Add global functions
    window.showGptPanel = showGptPanel;
    window.closeGptPanel = closeGptPanel;
    window.generateContent = generateContent;
    window.modifyContent = modifyContent;
    window.copyToClipboard = copyToClipboard;
    window.applyToComponent = applyToComponent;
    window.regenerateContent = regenerateContent;
    
    // Add editor commands for easier access
    editor.Commands.add('gpt-show-panel', {
      run: () => {
        console.log('🎯 Running gpt-show-panel command');
        showGptPanel();
      }
    });
    
    editor.Commands.add('gpt-close-panel', {
      run: () => {
        console.log('🎯 Running gpt-close-panel command');
        closeGptPanel();
      }
    });
    
    console.log('✅ GrapesJS GPT Plugin initialized successfully');
    console.log('✅ Global functions added to window:', {
      showGptPanel: !!window.showGptPanel,
      closeGptPanel: !!window.closeGptPanel,
      generateContent: !!window.generateContent
    });
    console.log('✅ GPT commands added: gpt-show-panel, gpt-close-panel');
  }

  // Initialize when editor is ready
  if (editor.isLoaded) {
    console.log('🎯 Editor is already loaded, initializing GPT plugin immediately');
    init();
  } else {
    console.log('🎯 Editor not loaded yet, waiting for load event');
    editor.on('load', () => {
      console.log('🎯 Editor load event fired, initializing GPT plugin');
      init();
    });
  }
  
  // Return plugin API
  return {
    showPanel: showGptPanel,
    closePanel: closeGptPanel,
    generateContent,
    modifyContent
  };
} 